import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { logLogin, logLogout } from '../services/auditService';
import { DEMO_USER, DEMO_PROFILE } from './DemoContext';

const AuthContext = createContext({});

export const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(IS_DEMO ? DEMO_USER : null);
  const [userProfile, setUserProfile] = useState(IS_DEMO ? DEMO_PROFILE : null);
  const [loading, setLoading] = useState(!IS_DEMO);
  const [sessionTimeout, setSessionTimeout] = useState(null);

  const TIMEOUT_DURATION = parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '900000');

  const signup = async (email, password, displayName, role = 'doctor') => {
    if (IS_DEMO) return DEMO_USER;
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await updateProfile(user, { displayName });
      const practiceId = user.uid;
      await setDoc(doc(db, 'practices', practiceId), {
        name: `${displayName}'s Practice`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      await setDoc(doc(db, 'practices', practiceId, 'users', user.uid), {
        email: user.email, displayName, role, practiceId,
        createdAt: serverTimestamp(), lastLogin: serverTimestamp()
      });
      return user;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    if (IS_DEMO) return DEMO_USER;
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      await loadUserProfile(user.uid);
      if (userProfile?.practiceId) {
        await logLogin(userProfile.practiceId, user.uid);
      }
      return user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    if (IS_DEMO) return;
    try {
      if (currentUser && userProfile) {
        await logLogout(userProfile.practiceId, currentUser.uid);
      }
      clearSessionTimeout();
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    if (IS_DEMO) return;
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  };

  const loadUserProfile = async (userId) => {
    if (IS_DEMO) return DEMO_PROFILE;
    try {
      const practiceId = userId;
      const userDocRef = doc(db, 'practices', practiceId, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const profile = { id: userId, ...userDoc.data() };
        setUserProfile(profile);
        return profile;
      } else {
        const user = auth.currentUser;
        if (!user) return null;
        const practiceDocRef = doc(db, 'practices', practiceId);
        const practiceDoc = await getDoc(practiceDocRef);
        if (!practiceDoc.exists()) {
          await setDoc(practiceDocRef, {
            name: `${user.displayName || user.email}'s Practice`,
            createdAt: serverTimestamp(), updatedAt: serverTimestamp()
          });
        }
        const defaultProfile = {
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          role: 'doctor', practiceId,
          createdAt: serverTimestamp(), lastLogin: serverTimestamp()
        };
        await setDoc(userDocRef, defaultProfile);
        const profile = { id: userId, ...defaultProfile };
        setUserProfile(profile);
        return profile;
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  };

  const setupSessionTimeout = () => {
    clearSessionTimeout();
    const timeoutId = setTimeout(() => {
      logout();
      alert('Your session has expired due to inactivity. Please log in again.');
    }, TIMEOUT_DURATION);
    setSessionTimeout(timeoutId);
  };

  const clearSessionTimeout = () => {
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
      setSessionTimeout(null);
    }
  };

  const resetSessionTimeout = () => {
    if (currentUser) setupSessionTimeout();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (IS_DEMO) return;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
        setupSessionTimeout();
      } else {
        setUserProfile(null);
        clearSessionTimeout();
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (IS_DEMO || !currentUser) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    const handleActivity = () => resetSessionTimeout();
    events.forEach(event => document.addEventListener(event, handleActivity, true));
    return () => events.forEach(event => document.removeEventListener(event, handleActivity, true));
  }, [currentUser]);

  const value = { currentUser, userProfile, signup, login, logout, resetPassword, loading };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
